import { Portal } from "@blueprintjs/core";
import { EventPayloadType } from "@package/ui-gql/index-analytics";
import {
  FlyoutEvent,
  FlyoutIframe,
  FlyoutPdf,
  FlyoutPost,
  FlyoutSearch,
  FlyoutStandard,
  FlyoutTrivia,
  FlyoutVideoGallery,
  FlyoutWipeAway,
  type FlyoutProps,
} from "@package/ui-wall-flyouts";
import { useCallback, useMemo } from "react";
import { SCREENS_DIV_ID } from "../../constants/constants";
import { useWallAnalytics } from "../../context/context-analytics/context-analytics";
import { useWallProvider } from "../../context/context-wall/context-wall";
import { FlyoutCloseButton } from "../FlyoutCloseButton/FlyoutCloseButton";
import { FlyoutQrCode } from "../FlyoutQrCode/FlyoutQrCode";

const flyouts = {
  "60042c78003c346c54a26814": FlyoutStandard,
  "60942b68093c366c54a2681a": FlyoutIframe,
  "60e5f1a78281542d34376c15": FlyoutPdf,
  "60942b68093c366c54a2681d": FlyoutSearch,
  "66a9203f29411eff6ca74e5b": FlyoutTrivia,
  "60942b68093c366c54a2681c": FlyoutVideoGallery,
  "60942b68093c366c54a26819": FlyoutPost,
  "60942b68093c366c54a2681f": FlyoutEvent,
  "60473c78003c346c84529564": FlyoutWipeAway,
  "61111c78003c346c54a26814": null,
  "60365c78003c344a72a26814": null,
};

export function ScreenFlyout({ screen }: FlyoutProps) {
  const { trigger } = useWallAnalytics();
  const provider = useWallProvider();
  const position =
    screen.flyout?.post?.flyoutPosition ?? screen.flyout?.position ?? undefined;
  const screenContainerDiv = useMemo(
    () => document.getElementById(SCREENS_DIV_ID),
    [],
  );
  const zoom = screen.flyout?.post?.flyoutScale ?? undefined;

  const Flyout =
    screen?.flyout?.post?.flyoutType &&
    flyouts[screen.flyout.post.flyoutType as keyof typeof flyouts];

  const onHandleMouse = useCallback(
    (x: number, y: number) => {
      if (screen.flyout?.post) {
        provider?.onFlyoutInteracted(screen.id, screen.flyout.post);
        trigger(
          EventPayloadType.PostInteracted,
          {
            screen: screen.index,
            position: {
              x,
              y,
            },
          },
          screen.flyout?.post?.id ?? undefined,
          screen.layout?.id ?? undefined,
        );
      }
    },
    [
      provider,
      screen.flyout?.post,
      screen.id,
      screen.index,
      screen.layout?.id,
      trigger,
    ],
  );

  if (!Flyout) {
    return null;
  }

  if (position && screenContainerDiv) {
    return (
      <Portal container={screenContainerDiv}>
        <div
          className="absolute z-10 bg-black"
          style={{
            top: position.y ?? 0,
            left: position.x ?? 0,
            width: position.width ?? 0,
            height: position.height ?? 0,
            zoom,
          }}
          onPointerDown={(e) => onHandleMouse(e.screenX, e.screenY)}
        >
          <Flyout screen={screen} />
          <FlyoutQrCode screen={screen} />
          <FlyoutCloseButton screen={screen} />
        </div>
      </Portal>
    );
  }

  return (
    <div
      className="absolute h-full w-full top-0 left-0 z-10 bg-black"
      onPointerDown={(e) => onHandleMouse(e.screenX, e.screenY)}
      style={{ zoom }}
    >
      <Flyout screen={screen} />
      <FlyoutQrCode screen={screen} />
      <FlyoutCloseButton screen={screen} />
    </div>
  );
}
